// POST /admin/dataengine/reconcile/floorplan_unit/merge
//
// Collapses N selected floorplan_unit rows into one. The survivor's
// unit_type is operator-chosen (defaults to the lowest-id input's
// unit_type). Areas/storeys/rooms/source-image arrays are merged
// server-side per MergeFloorplanUnits doc — the dialog just confirms
// the inputs and captures the reason.

import { useState, type CSSProperties } from "react";
import { Modal, inputBase } from "@/components/ui-prims";
import { ghostBtn, primaryBtn } from "@/components/admin-ui";
import { FLOORPLAN_UNIT_MAX_STOREYS, FLOORPLAN_UNIT_MIN_STOREYS } from "@/constants/reconcile";
import { ReasonField } from "./ReasonField";

// High-frequency merge reasons curated from staging operator behaviour
// and the PROP-65 / PROP-69 review threads. Order is "common first".
// The dialog still accepts free text via "Other…" so unusual cases
// (per-unit notes, follow-ups to a Jira comment) aren't blocked.
const MERGE_REASON_PRESETS: string[] = [
  "Duplicate row from extractor",
  "Same physical unit, multiple source images",
  "2-storey unit rendered as one row per floor",
  "Stacked plan recombination",
  "Floor-plate variant of same unit_type",
];

export type MergeInput = {
  id: number;
  unit_type: string;
  total_area_sqm?: number | null;
  n_storeys?: number | null;
};

export type MergeBody = {
  ids: number[];
  survivor_unit_type: string;
  // Operator overrides on the column-merge defaults. When omitted from
  // the wire, the server falls back to sum(area)/max(n_storeys); the
  // dialog pre-fills these with the computed defaults so most submits
  // carry non-null pointers equal to what the server would have
  // computed — engineering enforcement is the same either way.
  total_area_sqm?: number | null;
  n_storeys?: number | null;
  reason: string;
};


interface Props {
  open: boolean;
  inputs: MergeInput[];
  busy: boolean;
  onSubmit: (body: MergeBody) => void;
  onCancel: () => void;
}

export function MergeUnitsDialog({ open, inputs, busy, onSubmit, onCancel }: Props) {
  // Survivor defaults to the lowest-id input's unit_type — same row the
  // server picks as the survivor record. Operator can override.
  const sortedInputs = [...inputs].sort((a, b) => a.id - b.id);
  const defaultUT = sortedInputs[0]?.unit_type ?? "";

  return (
    <Modal
      open={open && inputs.length >= 2}
      onClose={busy ? () => {} : onCancel}
      title={`Merge ${inputs.length} units`}
      width={480}
    >
      <MergeForm
        key={inputs.map((i) => i.id).join(",")}
        inputs={sortedInputs}
        defaultUT={defaultUT}
        busy={busy}
        onSubmit={onSubmit}
        onCancel={onCancel}
      />
    </Modal>
  );
}

function MergeForm({ inputs, defaultUT, busy, onSubmit, onCancel }: { inputs: MergeInput[]; defaultUT: string; busy: boolean; onSubmit: Props["onSubmit"]; onCancel: () => void }) {
  // Computed defaults: server falls back to these when the override
  // fields are omitted. We pre-fill the inputs with them so the
  // operator sees what would have happened without their override,
  // then can edit (e.g. 2-storey duplex: bump n_storeys from 1→2).
  const sumArea = inputs.reduce((s, u) => s + (u.total_area_sqm ?? 0), 0);
  const maxStoreys = inputs.reduce((s, u) => Math.max(s, u.n_storeys ?? 0), 0);
  const defaultStoreys = Math.max(maxStoreys, FLOORPLAN_UNIT_MIN_STOREYS);

  const [survivor, setSurvivor] = useState(defaultUT);
  const [reason, setReason] = useState("");
  // String state for numeric fields so a temporarily-empty input
  // doesn't auto-coerce to 0 mid-edit. Parsed at submit time.
  const [areaStr, setAreaStr] = useState(String(round2(sumArea)));
  const [storeysStr, setStoreysStr] = useState(String(defaultStoreys));

  const survivorOk = survivor.trim() !== "";
  const reasonOk = reason.trim() !== "";
  const parsedArea = parseFloat(areaStr);
  const areaOk = Number.isFinite(parsedArea) && parsedArea >= 0;
  const parsedStoreys = parseInt(storeysStr, 10);
  const storeysOk = Number.isFinite(parsedStoreys) && parsedStoreys >= FLOORPLAN_UNIT_MIN_STOREYS && parsedStoreys <= FLOORPLAN_UNIT_MAX_STOREYS;
  const submittable = !busy && survivorOk && reasonOk && areaOk && storeysOk;

  function submit() {
    if (!submittable) return;
    onSubmit({
      ids: inputs.map((i) => i.id),
      survivor_unit_type: survivor.trim(),
      total_area_sqm: parsedArea,
      n_storeys: parsedStoreys,
      reason: reason.trim(),
    });
  }

  return (
    <>
      <div style={infoLine}>
        Computed defaults: areas sum to <strong>{round2(sumArea)} m²</strong>; max(n_storeys) = <strong>{maxStoreys || "—"}</strong>.
        Adjust below if the merged unit is actually different (e.g. a 2-storey duplex). Rooms + facing are reset (operator re-attributes after).
      </div>

      <div style={{ marginTop: 12, border: "1px solid var(--rule)", borderRadius: 7, overflow: "hidden" }}>
        {inputs.map((u, i) => (
          <div key={u.id} style={{
            display: "grid", gridTemplateColumns: "60px 1fr 90px 60px",
            gap: 8, padding: "8px 12px", alignItems: "center",
            background: i === 0 ? "var(--accent-soft)" : "var(--surface)",
            borderTop: i === 0 ? undefined : "1px solid var(--rule)",
            fontSize: 12,
          }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-4)" }}>#{u.id}</div>
            <div style={{ color: "var(--ink-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {u.unit_type || "—"}
              {i === 0 && <span style={{ marginLeft: 8, fontSize: 10.5, color: "var(--ink-4)" }}>(survivor)</span>}
            </div>
            <div style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>
              {u.total_area_sqm == null ? "—" : `${u.total_area_sqm} m²`}
            </div>
            <div style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>
              {u.n_storeys ?? "—"} st
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={fieldLabel}>Survivor unit_type *</span>
          <input
            style={inputBase}
            value={survivor}
            onChange={(e) => setSurvivor(e.target.value)}
            disabled={busy}
          />
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={fieldLabel}>Total area (m²) *</span>
            <input
              style={{ ...inputBase, fontFamily: "var(--font-mono)" }}
              type="number"
              min={0}
              step={0.1}
              value={areaStr}
              onChange={(e) => setAreaStr(e.target.value)}
              disabled={busy}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={fieldLabel}>
              n_storeys * <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>({FLOORPLAN_UNIT_MIN_STOREYS}–{FLOORPLAN_UNIT_MAX_STOREYS})</span>
            </span>
            <input
              style={{ ...inputBase, fontFamily: "var(--font-mono)" }}
              type="number"
              min={FLOORPLAN_UNIT_MIN_STOREYS}
              max={FLOORPLAN_UNIT_MAX_STOREYS}
              step={1}
              value={storeysStr}
              onChange={(e) => setStoreysStr(e.target.value)}
              disabled={busy}
            />
          </label>
        </div>

        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={fieldLabel}>Reason *</span>
          <ReasonField
            value={reason}
            onChange={setReason}
            presets={MERGE_REASON_PRESETS}
            placeholder="e.g. extractor split one logical plan across N floors — recombining"
            disabled={busy}
          />
        </label>
      </div>

      <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" style={ghostBtn} onClick={onCancel} disabled={busy}>Cancel</button>
        <button
          type="button"
          style={{ ...primaryBtn, opacity: submittable ? 1 : 0.5, cursor: submittable ? "pointer" : "not-allowed" }}
          onClick={submit}
          disabled={!submittable}
        >
          {busy ? "Merging…" : `Merge ${inputs.length} → 1`}
        </button>
      </div>
    </>
  );
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

const fieldLabel: CSSProperties = {
  fontFamily: "var(--font-ui)", fontSize: 10.5, fontWeight: 600,
  letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)",
};
const infoLine: CSSProperties = {
  fontSize: 12, color: "var(--ink-3)", lineHeight: 1.55,
};
